import { Box, Flex, SystemProps } from '@storyofams/react-ui'
import { startsWith } from 'lodash'
import { useRouter } from 'next/router'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { Button, Heading, Text, FixedRatio, QuantityInput } from '~components'
import { useShopify } from '~context'
import { trackFbAddToCart, gaAddToCartEvent } from '~lib'
import { ProductFullFragmentFragment, ProductVariant } from '~lib/shopify/sdk'
import { Image } from '../common/Image'

const messages = defineMessages({
  add: 'Add',
  addedToBox: 'is added to your box',
  viewBox: 'View box',
})

export const ImageWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  &:after {
    content: '';
    width: 100%;
    height: 100%;
  }
`

const ProductImage = ({ bg, src, ...props }) => (
  <FixedRatio bg={bg} mb={[1.5, 3]}>
    <ImageWrapper position="relative" p={bg && [1, 4]}>
      <Image
        height="100%"
        width="100%"
        layout="fill"
        objectFit="contain"
        quality={100}
        src={src}
        {...props}
      />
    </ImageWrapper>
  </FixedRatio>
)

interface ProductCardProps extends SystemProps {
  product: Pick<
    ProductFullFragmentFragment,
    'title' | 'images' | 'handle' | 'id' | 'description' | 'tags' | 'vendor'
  > & {
    variants: { edges: { node: { id: string; availableForSale: boolean } }[] }
  }
  withControls?: boolean
}

export const ProductCard = ({
  product,
  bg,
  withControls,
}: ProductCardProps) => {
  const { addItemToCart, portion, cart, toast, quantity } = useShopify()

  const router = useRouter()
  const intl = useIntl()

  const checkoutProduct = cart?.lines?.edges?.find(
    ({ node }) =>
      node.merchandise.id === product?.variants?.edges?.[0].node?.id,
  )?.node

  const isThemeBox = product?.tags?.includes('is_box')

  const productLink = isThemeBox
    ? `/theme-boxes/${product?.handle}`
    : `/meals/${product?.handle}${
        router?.query?.ordering ? '?ordering=true' : ''
      }`

  return (
    <Flex flexDirection="column" width="100%" bg={!withControls && bg}>
      <Button
        to={productLink}
        variant="unstyled"
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow="hidden"
        p={!withControls && [2, 4]}
      >
        <ProductImage
          src={product?.images?.edges?.[0]?.node?.url}
          bg={withControls && bg}
          mb={[1.5, 3]}
        />

        <Heading variant="sh4" as="h2" textAlign="center">
          {product?.title}
        </Heading>

        {!withControls && !!product?.description && (
          <Text textAlign="center" mt={[1, 2]}>
            {product?.description}
          </Text>
        )}
      </Button>

      {withControls && (
        <Box width="100%" mt={[1.5]} position="relative">
          {!!checkoutProduct ? (
            <QuantityInput {...checkoutProduct} />
          ) : (
            <Button
              variant="secondary"
              py={[1, 2]}
              height="auto"
              border="2px"
              width="100%"
              disabled={quantity >= 24}
              onClick={async () => {
                if (!portion) {
                  router.push('/order/select-box')
                  return
                }

                const lineItemType = product?.tags?.find(t =>
                  startsWith(t, 'is_'),
                )

                await addItemToCart(
                  [
                    {
                      quantity: 1,
                      merchandiseId: product?.variants?.edges?.[0].node?.id,
                    },
                  ],
                  lineItemType,
                )

                if (!!product) {
                  const productVariant = product?.variants?.edges?.[0]
                    .node as ProductVariant

                  gaAddToCartEvent({
                    currency: productVariant?.price?.currencyCode,
                    amount: productVariant?.price?.amount,
                    items: [
                      {
                        item_id: productVariant?.sku,
                        item_name: product?.title,
                        item_brand: product?.vendor,
                        item_variant: productVariant?.title,
                        price: Number(productVariant?.price?.amount),
                        quantity: 1,
                      },
                    ],
                  })
                }

                //trackFbAddToCart(product.title)
                toast(
                  `${product.title} ${intl.formatMessage(messages.addedToBox)}`,
                )
              }}
            >
              <FormattedMessage {...messages.add} />
            </Button>
          )}
        </Box>
      )}
      {isThemeBox && (
        <Button variant="primary" m={2} mt={0} to={productLink}>
          <FormattedMessage {...messages.viewBox} />
        </Button>
      )}
    </Flex>
  )
}
